import React, { useState, useEffect, useMemo } from 'react';
import { Grid, DialogContent, Button, TextField } from '@material-ui/core';
import * as Sentry from '@sentry/react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { ResponsiveSidebarDialog } from '@/styles/blocks';
import { colors } from '@/styles';
import { Body1, H2, H3, PrimaryButton } from './blocks';
import { useMutation } from '@apollo/client';
import { UPDATE_STOP_EVENTS_MANY } from './graphql';
import { format } from 'date-fns';
import { toArrayLiteral } from '@/utilities/toArrayLiteral';
import { buildStopCompletionEvents, buildStopCompletionTrackingEvents } from './completeStopHelpers';
import { COMPLETION_SOURCES } from '@/constants/completionSources';

const Header = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Row = styled.div`
    display: flex;
    margin-top: 1rem;
    justify-content: space-between;
`;

const CompleteStopModal = ({
    open,
    onClose,
    stop,
    route,
    setErrorMessage,
    setStopSaving,
    rescheduledStops,
    exceptionStops,
    cancelledStops,
    stopOrderMappings,
}) => {
    const [arrivalTime, setArrivalTime] = useState(null);
    const [completionTime, setCompletionTime] = useState(null);
    const [consigneeSignature, setConsigneeSignature] = useState(null);
    const [signatureTime, setSignatureTime] = useState(null);
    const [localError, setLocalError] = useState(null);
    const [pickupError, setPickupError] = useState(null);
    const [pods, setPods] = useState([]);
    const [markStopComplete, { loading }] = useMutation(UPDATE_STOP_EVENTS_MANY);

    useEffect(() => {
        if (stop?.arrival_time) {
            setArrivalTime(format(new Date(stop.arrival_time), "yyyy-MM-dd'T'HH:mm"));
        }
        if (stop?.stop_completion_time) {
            setCompletionTime(format(new Date(stop.stop_completion_time), "yyyy-MM-dd'T'HH:mm"));
        }
    }, [stop]);

    useEffect(() => {
        if (stop.type === 'DROPOFF') {
            const incompletePickups = route.stopsByRouteId.filter(
                (s) =>
                    s.type === 'PICKUP' &&
                    (stop.orders || []).some(
                        (order_id) => (s.orders || []).includes(order_id) && !s.stop_completion_time
                    )
            );
            if (incompletePickups.length > 0) {
                setPickupError(
                    `All pickup stops must be completed before a dropoff can be completed. Please complete the following stops: ${[
                        ...new Set(incompletePickups.map((stop) => stop.ordering)),
                    ]
                        .sort((a, b) => a - b)
                        .join(', ')}`
                );
            } else {
                setPickupError('');
            }
        }
    }, [route, stop]);

    const stopOrderMap = useMemo(
        () => (route?.orders || []).filter((mapping) => (stop.orders || []).includes(mapping.order_id)),
        [route?.orders, stop.orders]
    );

    const markRouteComplete = useMemo(() => {
        const incompleteStops = route?.stopsByRouteId?.filter(
            (s) =>
                !s?.stop_completion_time &&
                !exceptionStops.some(({ stop: { stop_id }, hasException }) => stop_id === s.stop_id && hasException) &&
                !cancelledStops.some(({ stop: { stop_id }, cancelled }) => stop_id === s.stop_id && cancelled)
        );
        if (incompleteStops.length === 1 && incompleteStops[0].stop_id === stop.stop_id) {
            return true;
        }
        return false;
    }, [route, exceptionStops, cancelledStops]);

    const handleMarkComplete = () => {
        try {
            setStopSaving(true);
            const completion_time = new Date().toISOString();
            let stopUpdates = [
                {
                    where: { stop_id: { _eq: stop.stop_id } },
                    _set: {
                        arrival_time: arrivalTime,
                        stop_completion_time: completionTime,
                        actions_completed: stop.orders ? toArrayLiteral(stop.orders) : null,
                    },
                },
            ];
            // Get the order ids that are to be marked as complete
            // If stop is type PICKUP, we want to set the orders as routedInProgress
            // If stop is type DROPOFF, we want to set the orders as routedCompleted

            let orderUpdates = [];
            let eventUpdates = [];
            let insertEvents = [];
            let routeUpdates = [];

            if (markRouteComplete) {
                routeUpdates = [
                    {
                        where: { route_id: { _eq: route.route_id } },
                        _set: {
                            status: 'complete',
                            completed: completion_time,
                            last_update_source: 'web',
                        },
                    },
                ];
            }

            // Walk the canonical lifecycle from each order's current state to the
            // (mappingType, stopType) target state. See ./completeStopHelpers.js.
            insertEvents.push(...buildStopCompletionEvents({ stopOrderMap, stopType: stop.type }));

            // Per-item timeline rows for the same completion, in the same mutation.
            const trackingEvents = buildStopCompletionTrackingEvents({ stopOrderMap, stopType: stop.type });

            // Second pass: build order-level metadata updates (order_status,
            // ltl_pickup_completed, ltl_dropoff_arrival, completion_time) — these
            // touch orders rather than wh_events and follow different rules per
            // (mappingType, stopType).
            stopOrderMap.forEach(({ type: mappingType, order }) => {
                if (stop.type === 'PICKUP') {
                    if (mappingType === 'PICKUP') {
                        // Crossdock first leg, customer-origin pickup. Stamp the
                        // ltl_pickup_completed metadata; the wh_event chain (advance
                        // walked to CD_PENDING) is the source of truth for state.
                        orderUpdates.push({
                            where: { order_id: { _eq: order.order_id } },
                            _set: {
                                ltl_pickup_completed: completionTime,
                                last_update_source: 'web',
                            },
                        });
                    } else {
                        // Crossdock second leg (warehouse pickup) or E2E pickup —
                        // mark order in-progress.
                        orderUpdates.push({
                            where: { order_id: { _eq: order.order_id } },
                            _set: {
                                order_status: 'routedInProgress',
                                last_update_source: 'web',
                            },
                        });
                    }
                } else if (stop.type === 'DROPOFF') {
                    if (mappingType === 'PICKUP') {
                        // Crossdock first leg, warehouse drop. No order-level update
                        // needed — the advance walk to CD_RECEIVED is the only effect.
                    } else {
                        // Crossdock second leg or E2E dropoff — record arrival/
                        // completion times and mark routed-complete.
                        orderUpdates.push({
                            where: { order_id: { _eq: order.order_id } },
                            _set: {
                                ltl_dropoff_arrival: arrivalTime || new Date().toISOString(),
                                last_update_source: 'web',
                                completion_time: completionTime,
                                order_status: 'routedComplete',
                                completion_source: COMPLETION_SOURCES.WEB,
                            },
                        });
                    }
                }
            });

            markStopComplete({
                variables: {
                    routeUpdates: routeUpdates,
                    orderUpdates: orderUpdates,
                    stopUpdates: stopUpdates,
                    eventUpdates: eventUpdates,
                    insertEvents: insertEvents,
                    trackingEvents: trackingEvents,
                },
                onError: (error) => {
                    setErrorMessage('Looks like something went wrong. We were unable to complete your stop.');
                    Sentry.captureException(error);
                    console.error(error.message);
                },
                onCompleted: () => {
                    onClose();
                },
            });
        } catch (error) {
            setErrorMessage('Looks like something went wrong. We were unable to complete your stop.');
            console.error(error);
            Sentry.captureException(error);
        } finally {
            setStopSaving(false);
            onClose();
        }
    };

    return (
        <ResponsiveSidebarDialog
            // collapsed={collapseSideNav}
            open={open}
            onClose={onClose}
        >
            <DialogContent>
                <Header>
                    <H2>Are you sure you want to manually complete the stop?</H2>
                </Header>
                <Body1>This cannot be undone.</Body1>
                <Grid>
                    <Row>
                        <TextField
                            fullWidth
                            label="Arrival Time"
                            type="datetime-local"
                            variant="outlined"
                            value={arrivalTime ? format(new Date(arrivalTime), "yyyy-MM-dd'T'HH:mm") : ''}
                            onChange={(e) => {
                                let dateStr = null;
                                if (e.target.value) {
                                    dateStr = new Date(e.target.value).toISOString();
                                    if (completionTime && new Date(dateStr) > new Date(completionTime)) {
                                        setLocalError('Arrival time cannot be after completion time');
                                    } else {
                                        setLocalError('');
                                    }
                                }
                                setArrivalTime(dateStr);
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!localError}
                            helperText={localError}
                        />
                    </Row>
                    <Row>
                        <TextField
                            fullWidth
                            label="Completion Time"
                            type="datetime-local"
                            variant="outlined"
                            value={completionTime ? format(new Date(completionTime), "yyyy-MM-dd'T'HH:mm") : ''}
                            onChange={(e) => {
                                let dateStr = null;
                                if (e.target.value) {
                                    const selectedDate = new Date(e.target.value);
                                    if (arrivalTime && selectedDate > new Date(arrivalTime)) {
                                        dateStr = selectedDate.toISOString();
                                    }
                                }
                                setCompletionTime(dateStr);
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            disabled={!arrivalTime}
                            InputProps={{
                                min: arrivalTime ? format(new Date(arrivalTime), "yyyy-MM-dd'T'HH:mm") : undefined,
                                max: '9999-12-31T23:59',
                            }}
                        />
                    </Row>
                    {/* <Row>
                        <TextField
                            fullWidth
                            label="Consignee Signature (Print Name)"
                            variant="outlined"
                            value={consigneeSignature}
                            onChange={(e) => setConsigneeSignature(e.target.value)}
                        />
                    </Row>
                    <Row>
                        <TextField
                            fullWidth
                            label="Signature Time"
                            type="datetime-local"
                            variant="outlined"
                            value={signatureTime ? format(new Date(signatureTime), "yyyy-MM-dd'T'HH:mm") : ''}
                            onChange={(e) => {
                                let dateStr = null;
                                if (e.target.value) {
                                    dateStr = new Date(e.target.value).toISOString();
                                }
                                setSignatureTime(dateStr);
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Row> */}

                    <Row>
                        <Body1
                            css={css`
                                color: ${colors.greys.primary};
                            `}
                        >
                            Note: This should be reserved only for situations where the stop is in a stuck state due to
                            driver app issues.
                        </Body1>
                    </Row>
                    {pickupError && (
                        <Row>
                            <Body1
                                css={css`
                                    color: ${colors.reds.primary};
                                `}
                            >
                                {pickupError}
                            </Body1>
                        </Row>
                    )}

                    <Row>
                        <Button variant="outlined" onClick={onClose}>
                            <H3>Cancel</H3>
                        </Button>
                        <PrimaryButton
                            variant="contained"
                            onClick={handleMarkComplete}
                            disabled={!arrivalTime || !completionTime || localError || loading || pickupError}
                        >
                            <H3>Submit</H3>
                        </PrimaryButton>
                    </Row>
                </Grid>
            </DialogContent>
        </ResponsiveSidebarDialog>
    );
};

export default CompleteStopModal;
